import { useState, useEffect } from 'react';
import { ChevronLeft, Save, User, Building2, CheckCircle, Upload, X, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { SignedImage, SignedLink } from '../common/SignedFile';

interface EditDriverPageProps {
  driverId: string;
}

export function EditDriverPage({ driverId }: EditDriverPageProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Required fields only
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cdlNumber, setCdlNumber] = useState('');
  const [cdlState, setCdlState] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [cdlExpiration, setCdlExpiration] = useState('');
  const [mvrNotes, setMvrNotes] = useState('');
  const [mvrStatus, setMvrStatus] = useState('CLEAN');
  const [mvrExpiration, setMvrExpiration] = useState('');
  const [clearinghouseExpiration, setClearinghouseExpiration] = useState('');
  const [medicalExpiration, setMedicalExpiration] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [status, setStatus] = useState('active');
  const [terminationDate, setTerminationDate] = useState('');
  const [terminationLetter, setTerminationLetter] = useState('');
  const [terminationLetterFile, setTerminationLetterFile] = useState<File | null>(null);
  const [driverType, setDriverType] = useState<'company_driver' | 'owner_operator' | 'rent' | 'buy'>('company_driver');
  const [fuelCardNumber, setFuelCardNumber] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState<any[]>([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [ssn, setSsn] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [needsOfficeVisit, setNeedsOfficeVisit] = useState(false);
  const [officeVisitReason, setOfficeVisitReason] = useState('');
  const [officeVisitCompletedAt, setOfficeVisitCompletedAt] = useState<string | null>(null);
  const [showOfficeVisitModal, setShowOfficeVisitModal] = useState(false);
  const [officeVisitReasonInput, setOfficeVisitReasonInput] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    loadDriverData();
    loadCompanies();
  }, [driverId]);

  async function loadDriverData() {
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('*, companies(id, name, code)')
        .eq('id', driverId)
        .single();

      if (error) throw error;

      setFirstName(data.first_name || '');
      setLastName(data.last_name || '');
      setCdlNumber(data.cdl_number || '');
      setCdlState(data.cdl_state || '');
      setYearsOfExperience(data.years_of_experience?.toString() || '');
      setDateOfBirth('');
      setCdlExpiration(data.cdl_expiration || '');
      setMvrNotes(data.mvr_notes || '');
      setMvrStatus(data.mvr_status || 'CLEAN');
      setMvrExpiration(data.mvr_expiration_date || '');
      setClearinghouseExpiration(data.clearinghouse_expiration_date || '');
      setMedicalExpiration(data.medical_card_expiration || '');
      setHireDate(data.hire_date || '');
      setStatus(data.driver_status || 'ACTIVE');
      setTerminationDate(data.termination_date || '');
      setTerminationLetter(data.termination_letter || '');
      setDriverType(data.driver_type || 'company_driver');
      setFuelCardNumber(data.fuel_card_number || '');
      setCompanyId(data.company_id || '');
      setPhoneNumber(data.phone_number || '');
      setSsn(data.ssn || '');
      setAddress(data.address || '');
      setCity(data.city || '');
      setState(data.state || '');
      setZipCode(data.zip_code || '');
      setNeedsOfficeVisit(data.needs_office_visit || false);
      setOfficeVisitReason(data.office_visit_reason || '');
      setOfficeVisitCompletedAt(data.office_visit_completed_at || null);
      setUserId(data.user_id || null);

      // Load user role if user_id exists
      if (data.user_id) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', data.user_id)
          .single();

        if (!profileError && profileData) {
          setUserRole(profileData.role || '');
        }
      }
    } catch (error) {
      console.error('Error loading driver:', error);
      toast.error('Failed to load driver data');
    } finally {
      setLoading(false);
    }
  }

  async function loadCompanies() {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, code')
        .order('name');

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName || !lastName) {
      toast.error('First name and last name are required');
      return;
    }
    
    setSaving(true);

    try {
      let terminationLetterUrl = terminationLetter;

      // Upload termination letter file if selected
      if (terminationLetterFile) {
        const fileExt = terminationLetterFile.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `termination-letters/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, terminationLetterFile);

        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          toast.error('Failed to upload termination letter');
          setSaving(false);
          return;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('company-documents')
          .getPublicUrl(filePath);

        terminationLetterUrl = publicUrl;
      }

      const { error } = await supabase
        .from('drivers')
        .update({
          first_name: firstName,
          last_name: lastName,
          cdl_number: cdlNumber || null,
          cdl_state: cdlState || null,
          years_of_experience: yearsOfExperience ? parseInt(yearsOfExperience) : null,
          cdl_expiration: cdlExpiration || null,
          ssn: ssn || null,
          address: address || null,
          city: city || null,
          state: state || null,
          zip_code: zipCode || null,
          mvr_notes: mvrNotes || null,
          mvr_status: mvrStatus,
          mvr_expiration_date: mvrExpiration || null,
          clearinghouse_expiration_date: clearinghouseExpiration || null,
          medical_card_expiration: medicalExpiration || null,
          hire_date: hireDate || null,
          driver_status: status,
          termination_date: terminationDate || null,
          termination_letter: terminationLetterUrl || null,
          is_active: status === 'active',
          driver_type: driverType,
          fuel_card_number: fuelCardNumber || null,
          company_id: companyId || null,
          phone_number: phoneNumber || null,
        })
        .eq('id', driverId);

      if (error) throw error;

      // Update user role if userId exists and role has changed
      if (userId && userRole) {
        const { error: roleError } = await supabase
          .from('profiles')
          .update({ role: userRole })
          .eq('id', userId);

        if (roleError) {
          console.error('Error updating user role:', roleError);
          toast.error('Driver updated but failed to update role');
          setSaving(false);
          return;
        }
      }

      toast.success('Driver updated successfully!');
      navigate(`/drivers/${driverId}`);
    } catch (error) {
      console.error('Error updating driver:', error);
      toast.error('Failed to update driver');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkForOfficeVisit = async () => {
    if (!officeVisitReasonInput.trim()) {
      toast.error('Please provide a reason for the office visit');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('drivers')
        .update({
          needs_office_visit: true,
          office_visit_reason: officeVisitReasonInput,
          office_visit_marked_at: new Date().toISOString(),
          office_visit_marked_by: user.id,
          office_visit_completed_at: null,
          office_visit_completed_by: null
        })
        .eq('id', driverId);

      if (error) throw error;

      toast.success('Driver marked for office visit');
      setShowOfficeVisitModal(false);
      setOfficeVisitReasonInput('');
      loadDriverData();
    } catch (error) {
      console.error('Error marking driver for office visit:', error);
      toast.error('Failed to mark driver for office visit');
    }
  };

  const handleCompleteOfficeVisit = async () => {
    if (!confirm('Mark this office visit as completed?')) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('drivers')
        .update({
          needs_office_visit: false,
          office_visit_completed_at: new Date().toISOString(),
          office_visit_completed_by: user.id
        })
        .eq('id', driverId);

      if (error) throw error;

      toast.success('Office visit marked as completed');
      loadDriverData();
    } catch (error) {
      console.error('Error completing office visit:', error);
      toast.error('Failed to complete office visit');
    }
  };

  const handleCancelOfficeVisit = async () => {
    if (!confirm('Cancel this office visit requirement?')) return;

    try {
      const { error } = await supabase
        .from('drivers')
        .update({
          needs_office_visit: false,
          office_visit_reason: null,
          office_visit_marked_at: null,
          office_visit_marked_by: null
        })
        .eq('id', driverId);

      if (error) throw error;

      toast.success('Office visit requirement cancelled');
      loadDriverData();
    } catch (error) {
      console.error('Error cancelling office visit:', error);
      toast.error('Failed to cancel office visit');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/drivers')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Edit Driver</h1>
                  <p className="text-sm text-slate-500">{firstName} {lastName}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {needsOfficeVisit && !officeVisitCompletedAt && (
                <>
                  <button
                    onClick={handleCompleteOfficeVisit}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Mark Visit Complete</span>
                  </button>
                  <button
                    onClick={handleCancelOfficeVisit}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-medium">Cancel Visit</span>
                  </button>
                </>
              )}
              {!needsOfficeVisit && (
                <button
                  onClick={() => setShowOfficeVisitModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <Building2 className="w-5 h-5" />
                  <span className="font-medium">Mark for Office Visit</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Fuel Card Number
                </label>
                <input
                  type="text"
                  value={fuelCardNumber}
                  onChange={(e) => setFuelCardNumber(e.target.value)}
                  placeholder="e.g., 052"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Years of Experience
                </label>
                <input
                  type="number"
                  value={yearsOfExperience}
                  onChange={(e) => setYearsOfExperience(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  SSN
                </label>
                <input
                  type="text"
                  value={ssn}
                  onChange={(e) => setSsn(e.target.value)}
                  placeholder="XXX-XX-XXXX"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street Address"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="City"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  placeholder="State"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  placeholder="ZIP Code"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company
                </label>
                <select
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a company...</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* CDL Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">CDL Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  CDL Number
                </label>
                <input
                  type="text"
                  value={cdlNumber}
                  onChange={(e) => setCdlNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  CDL State
                </label>
                <input
                  type="text"
                  value={cdlState}
                  onChange={(e) => setCdlState(e.target.value)}
                  placeholder="e.g., FL, NY, CA"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  CDL Expiration
                </label>
                <input
                  type="date"
                  value={cdlExpiration}
                  onChange={(e) => setCdlExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* MVR & Medical */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">MVR & Medical</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  MVR Notes
                </label>
                <textarea
                  value={mvrNotes}
                  onChange={(e) => setMvrNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  MVR Status
                </label>
                <select
                  value={mvrStatus}
                  onChange={(e) => setMvrStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="CLEAN">CLEAN</option>
                  <option value="NOT CLEAN">NOT CLEAN</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  MVR Expiration
                </label>
                <input
                  type="date"
                  value={mvrExpiration}
                  onChange={(e) => setMvrExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Clearinghouse Expiration
                </label>
                <input
                  type="date"
                  value={clearinghouseExpiration}
                  onChange={(e) => setClearinghouseExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Medical Expiration
                </label>
                <input
                  type="date"
                  value={medicalExpiration}
                  onChange={(e) => setMedicalExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Employment */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Employment</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Hire Date
                </label>
                <input
                  type="date"
                  value={hireDate}
                  onChange={(e) => setHireDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company
                </label>
                <select
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Unassigned</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Driver Type
                </label>
                <select
                  value={driverType}
                  onChange={(e) => setDriverType(e.target.value as any)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="company_driver">Company Driver</option>
                  <option value="owner_operator">Owner Operator</option>
                  <option value="rent">Rent</option>
                  <option value="buy">Buy</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">New Drivers</option>
                  <option value="vacation">Vacation</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>

              {userId && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    User Role
                  </label>
                  <select
                    value={userRole}
                    onChange={(e) => setUserRole(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No Role</option>
                    <option value="Superadmin">Superadmin</option>
                    <option value="Admin">Admin</option>
                    <option value="Dispatch">Dispatch</option>
                    <option value="Safety">Safety Manager</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Accounting">Accounting</option>
                    <option value="Tracking">Tracking</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Termination Date
                </label>
                <input
                  type="date"
                  value={terminationDate}
                  onChange={(e) => setTerminationDate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Termination Letter
                </label>
                <div className="space-y-2">
                  {terminationLetter && !terminationLetterFile && (
                    <div className="space-y-2">
                      <div className="relative group">
                        <SignedImage
                          stored={terminationLetter}
                          alt="Current Termination Letter"
                          className="w-full h-48 object-cover rounded-lg border border-slate-200"
                        />
                      </div>
                      <div className="flex gap-2">
                        <SignedLink
                          stored={terminationLetter}
                          className="flex-1 text-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          View Current
                        </SignedLink>
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this termination letter?')) {
                              setTerminationLetter('');
                            }
                          }}
                          className="flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                  {terminationLetterFile ? (
                    <div className="flex items-center justify-between bg-slate-50 px-4 py-3 rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2">
                        <Upload className="w-4 h-4 text-slate-600" />
                        <span className="text-sm text-slate-700">{terminationLetterFile.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setTerminationLetterFile(null)}
                        className="p-1 hover:bg-slate-200 rounded transition-colors"
                      >
                        <X className="w-4 h-4 text-slate-600" />
                      </button>
                    </div>
                  ) : !terminationLetter && (
                    <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                      <Upload className="w-4 h-4 text-slate-600" />
                      <span className="text-sm text-slate-600">Upload Termination Letter</span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setTerminationLetterFile(e.target.files[0]);
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  )}
                  {!terminationLetter && !terminationLetterFile && (
                    <p className="text-xs text-slate-500">PDF, DOC, DOCX, JPG, PNG (max 10MB)</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-6">
            <button
              type="button"
              onClick={() => navigate('/drivers')}
              className="px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Office Visit Status Alert */}
        {needsOfficeVisit && !officeVisitCompletedAt && (
          <div className="mt-6 bg-red-50 border-2 border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Building2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">Office Visit Required</h3>
                <p className="text-sm text-red-700 mb-2">{officeVisitReason || 'No reason provided'}</p>
                <p className="text-xs text-red-600">This driver's name will appear with a red background in the dispatch board.</p>
              </div>
            </div>
          </div>
        )}

        {officeVisitCompletedAt && (
          <div className="mt-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-green-900 mb-1">Office Visit Completed</h3>
                <p className="text-sm text-green-700">Completed on {new Date(officeVisitCompletedAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Office Visit Modal */}
      {showOfficeVisitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">Mark for Office Visit</h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Reason for Office Visit <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={officeVisitReasonInput}
                  onChange={(e) => setOfficeVisitReasonInput(e.target.value)}
                  placeholder="Enter reason why driver needs to come to Chicago office..."
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-sm text-orange-800">
                  Driver's name will appear with a red background in the dispatch board.
                </p>
              </div>
            </div>

            <div className="px-6 py-4 bg-slate-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => {
                  setShowOfficeVisitModal(false);
                  setOfficeVisitReasonInput('');
                }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkForOfficeVisit}
                disabled={!officeVisitReasonInput.trim()}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Mark for Visit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
